import React, { useState, useEffect } from 'react'
import {
  Box,
  Container,
  Typography,
  Paper,
  Alert,
  CircularProgress,
  Tabs,
  Tab,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Button,
} from '@mui/material'
import {
  Search as SearchIcon,
  FilterList as FilterIcon,
} from '@mui/icons-material'
import { AgencyServicePackageList } from '../components/AgencyServicePackageForm/AgencyServicePackageList'
import { ServicePackageListItem } from '../types/servicePackage'
import {

  fetchServicePackagesAgencyProdiverId,
  fetchSpecializations,
  SpecializationOption,
} from '../services/servicePackageService'
import { useParams } from 'react-router-dom'
import { log } from 'console'

type TabValue = 'all' | 'published' | 'draft' | 'archived'

export const AgencyServicePackageListPage: React.FC = () => {
  const [loading, setLoading] = useState(true)
  const [servicePackages, setServicePackages] = useState<
    ServicePackageListItem[]
  >([])
  const [activeTab, setActiveTab] = useState<TabValue>('all')
  const [error, setError] = useState<string | null>(null)
  const [searchQuery, setSearchQuery] = useState<string>('')
  const [selectedSpecialization, setSelectedSpecialization] =
    useState<string>('')
  const [specializations, setSpecializations] = useState<
    SpecializationOption[]
  >([])
  const [specializationsLoading, setSpecializationsLoading] = useState(false)
  const [page, setPage] = useState(1)
  const [limit, setLimit] = useState(10)
  const [totalRecords, setTotalRecords] = useState(0)
  const { id } = useParams();

  console.log("ididid", id);

  const fetchData = async () => {
    try {
      setLoading(true)
      setError(null)
      const params: any = {
        page,
        limit,
        status: activeTab,
      }

      if (searchQuery?.trim()) {
        params.search = searchQuery.trim()
      }

      if (selectedSpecialization) {
        params.speciality = selectedSpecialization
      }
      if (id) {
        params.therapistId = id
      }

      const response = await fetchServicePackagesAgencyProdiverId(params)
      setServicePackages(response.services)
      setTotalRecords(response.totalRecords || 0)
    } catch (err) {
      setError('Failed to load service packages. Please try again.')
      console.error('Error fetching service packages:', err)
    } finally {
      setLoading(false)
    }
  }

  const loadSpecializations = async () => {
    try {
      setSpecializationsLoading(true)
      const specs = await fetchSpecializations()
      setSpecializations(specs)
    } catch (err) {
      console.error('Error fetching specializations:', err)
    } finally {
      setSpecializationsLoading(false)
    }
  }

  useEffect(() => {
    loadSpecializations()
  }, [])

  useEffect(() => {
    fetchData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, page, limit, selectedSpecialization])

  // Debounce search query
  useEffect(() => {
    const timer = setTimeout(() => {
      if (page === 1) {
        fetchData()
      } else {
        setPage(1)
      }
    }, 500)

    return () => clearTimeout(timer)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery])

  const handleTabChange = (event: React.SyntheticEvent, newValue: TabValue) => {
    setActiveTab(newValue)
    setPage(1)
  }

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(event.target.value)
  }

  const handleSpecializationChange = (event: any) => {
    setSelectedSpecialization(event.target.value)
    setPage(1)
  }

  const clearFilters = () => {
    setSearchQuery('')
    setSelectedSpecialization('')
    setPage(1)
  }

  const handlePageChange = (
    event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number
  ) => {
    setPage(newPage + 1) // Convert from 0-indexed to 1-indexed
  }

  const handleRowsPerPageChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const newLimit = parseInt(event.target.value, 10)
    setLimit(newLimit)
    setPage(1) // Reset to first page when changing page size
  }



  return (
    <Box sx={{ bgcolor: 'background.default', minHeight: '100vh', py: 4 }}>
      <Container maxWidth="lg">
        <Paper sx={{ p: 3, mb: 3 }}>
          {/* Header Section */}
          <Box sx={{ mb: 3 }}>
            <Typography
              variant="h4"
              color="primary.main"
              fontWeight={600}
              gutterBottom
            >
              Service Packages
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Create and manage your therapy service offerings. Customize
              pricing, scheduling, and visibility settings for each package.
            </Typography>
          </Box>

          {/* Filters Section */}
          <Box sx={{ mb: 3 }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                mb: 2,
              }}
            >
              <FilterIcon color="action" />
              <Typography variant="h6" sx={{ fontWeight: 500 }}>
                Filters
              </Typography>
            </Box>

            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  fullWidth
                  placeholder="Search packages..."
                  value={searchQuery}
                  onChange={handleSearchChange}
                  size="small"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon color="action" />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                    },
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6} md={4}>
                <FormControl fullWidth size="small">
                  <InputLabel id="specialization-select-label">
                    Specialization
                  </InputLabel>
                  <Select
                    labelId="specialization-select-label"
                    value={selectedSpecialization}
                    label="Specialization"
                    onChange={handleSpecializationChange}
                    disabled={specializationsLoading}
                    sx={{
                      borderRadius: 2,
                    }}
                  >
                    <MenuItem value="">
                      <em>All Specializations</em>
                    </MenuItem>
                    {specializations.map(spec => (
                      <MenuItem key={spec.id} value={spec.id}>
                        {spec.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid
                item
                xs={12}
                sm={12}
                md={4}
                sx={{
                  display: 'flex',
                  justifyContent: { xs: 'stretch', md: 'flex-start' },
                  alignItems: 'center',
                }}
              >
                <Button
                  variant="outlined"
                  color="primary"
                  onClick={clearFilters}
                  sx={{
                    width: { xs: '100%', md: 'auto' },
                    borderRadius: 2,
                    fontWeight: 600,
                    textTransform: 'none',
                    py: 1,
                    px: 2.5,
                  }}
                >
                  Clear Filters
                </Button>
              </Grid>
            </Grid>
          </Box>

          {/* Tabs Section */}
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            indicatorColor="primary"
            textColor="primary"
            sx={{
              borderBottom: 1,
              borderColor: 'divider',
              '& .MuiTab-root': {
                textTransform: 'none',
                fontWeight: 500,
              },
            }}
          >
            <Tab label="All Packages" value="all" />
            <Tab label="Published" value="published" />
            <Tab label="Drafts" value="draft" />
          </Tabs>
        </Paper>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 5 }}>
            <CircularProgress />
          </Box>
        ) : (
          <AgencyServicePackageList
            servicePackages={servicePackages}
            onRefresh={fetchData}
            totalRecords={totalRecords}
            activeTab={activeTab}
            hasFilters={!!(searchQuery.trim() || selectedSpecialization)}
            page={page - 1} // Convert from 1-indexed to 0-indexed for TablePagination
            rowsPerPage={limit}
            onPageChange={handlePageChange}
            onRowsPerPageChange={handleRowsPerPageChange}
            providerId={id}
          />
        )}
      </Container>
    </Box>
  )
}

export default AgencyServicePackageListPage
